import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Theme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'opsight-theme';

interface ThemeContextType {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
  setLightTheme: () => void;
  setDarkTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [theme, setTheme] = useState<Theme>(() => {
    // Check localStorage first
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme;
    if (storedTheme === 'light' || storedTheme === 'dark') {
      return storedTheme;
    }

    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }

    // Default to dark
    return 'dark';
  });

  // Apply theme immediately on mount and when it changes
  useEffect(() => {
    // Apply theme to document root, html and body
    const root = document.documentElement;
    const html = document.documentElement;
    const body = document.body;
    
    if (theme === 'dark') {
      root.setAttribute('data-theme', 'dark');
      html.classList.add('theme-dark');
      html.classList.remove('theme-light');
      body.classList.add('theme-dark');
      body.classList.remove('theme-light');
      body.style.backgroundColor = '#0B1220';
      body.style.color = '#F8FAFC';
    } else {
      root.removeAttribute('data-theme');
      html.classList.add('theme-light');
      html.classList.remove('theme-dark');
      body.classList.add('theme-light');
      body.classList.remove('theme-dark');
      body.style.backgroundColor = '#FBFBFB';
      body.style.color = '#0B1220';
    }

    // Persist to localStorage
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  const setLightTheme = () => {
    setTheme('light');
  };

  const setDarkTheme = () => {
    setTheme('dark');
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        isDark: theme === 'dark',
        toggleTheme,
        setLightTheme,
        setDarkTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};


